import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowRight, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const TickerSearch: React.FC = () => {
  const navigate = useNavigate();
  const { isPro, loading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    if (authLoading) return;

    setIsSearching(true);
    setError(null);
    
    const ticker = searchQuery.trim().toUpperCase();
    
    // Validate ticker format
    if (!/^[A-Z]{1,5}$/.test(ticker)) {
      setError('Invalid ticker format. Use 1-5 uppercase letters.');
      setIsSearching(false);
      return;
    }

    try {
      // Check if ticker exists via API
      await apiService.getAnalytics(ticker, 12, 60, isPro);
      
      // If ticker found, navigate to ticker page
      navigate(`/ticker/${ticker}`);
      
      // Reset state
      setSearchQuery('');
    } catch (error) {
      // Error handling
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          setError('Ticker not found');
        } else if (error.message.includes('no data')) {
          setError('No data available for the selected period');
        } else {
          setError('Error searching for ticker');
        }
      } else {
        setError('Error searching for ticker');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.toUpperCase());
  };

  return (
    <form onSubmit={handleSearch} className="w-full">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder="Search ticker (e.g., SPY, TSLA, AAPL)"
          className="input-glass ticker-search-input w-full pl-4 pr-12 py-3 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50 border transition-colors uppercase"
          maxLength={5}
        />
        <button
          type="submit"
          disabled={isSearching || !searchQuery.trim()}
          className="ticker-search-submit absolute inset-y-0 right-0 px-4 flex items-center text-black font-semibold rounded-r-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSearching ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Search className="w-4 h-4" />
            </motion.div>
          ) : (
            <ArrowRight className="w-4 h-4" />
          )}
        </button>
      </div>
      {error && (
        <div className="mt-2 flex items-center space-x-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
};

export default TickerSearch; 
